import { yupResolver } from "@hookform/resolvers/yup";
import { TFunction } from "i18next";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

import { Badge, Button, InputText, Modal, Text } from "@pentabd/ui";

import { ErrorModal, SectionTitle, SuccessModal } from "@components";

import { acceptExtraResponsibility } from "@api/employee";
import {
  AcceptExtraResponsibilityFormDataType,
  EXTRA_RESPONSIBILITY,
  acceptExtraResponsibilityValidation,
} from "@validations/HRM/employee/extraResponsibilityValidation";

import { STATUS } from "@constants/status.hrm";
import { OutletContextType } from "@type/layout";

const { SUCCESS, FAILED } = STATUS;

interface PendingAccountModalType {
  isModalOpen: boolean;
  setIsPendingAccountModalOpen: (arg: boolean) => void;
  action?: () => void;
  pendingData: any;
  t: TFunction<"translation", undefined>;
  id: number;
}

export const PendingAccountModal = ({
  isModalOpen,
  setIsPendingAccountModalOpen,
  action,
  pendingData,
  t,
  id,
}: PendingAccountModalType) => {
  const PendingAccount = ({ data }: { data: any }) => {
    const [isSuccessModalOpen, setIsSuccessModalOpen] =
      useState<boolean>(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
    const [message, setMessage] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { onRefetchHeaderState } = useOutletContext<OutletContextType>();

    const methods = useForm<AcceptExtraResponsibilityFormDataType>({
      resolver: yupResolver(acceptExtraResponsibilityValidation),
    });

    const {
      handleSubmit,
      register,
      reset,
      formState: { errors },
    } = methods;

    const successAction = () => {
      reset();
      setIsSuccessModalOpen(false);
      setIsPendingAccountModalOpen(false);
      setMessage("");
      onRefetchHeaderState();
    };

    const closeErrorModal = () => {
      setIsErrorModalOpen(false);
      setMessage("");
    };

    useEffect(() => {
      const reset = () => {
        setIsErrorModalOpen(false);
        setIsSuccessModalOpen(false);
        setMessage("");
      };
      return reset();
    }, []);

    const onSubmit: SubmitHandler<AcceptExtraResponsibilityFormDataType> = (
      formData: any,
    ) => {
      setIsLoading(true);
      acceptExtraResponsibility(
        id,
        data.tempAccMasterId, // tempAccountId
        { username: data.email, ...formData },
        (res) => {
          if (res.status === SUCCESS) {
            setIsSuccessModalOpen(true);
            setMessage(res.message);
          } else if (res.status === FAILED) {
            setMessage(res.message);
            setIsErrorModalOpen(true);
          }
          setIsLoading(false);
        },
      );
    };

    return (
      <>
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={successAction}
          subtitle={message}
        />
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
          subtitle={message}
        />
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="border p-4 rounded"
          >
            <div className="row m-5 d-flex align-items-center justify-content-between gap-6 bg-light px-6 py-4 rounded">
              <div className="d-flex align-items-center  gap-8">
                <Text color="subtitle1" weight="semibold">
                  {t("EMPLOYEE.PENDING_MODAL.NEW_ROLE")}:
                </Text>
                <Badge
                  label={data.position}
                  type="info"
                  size="lg"
                  className="w-max"
                />
              </div>
              <Button
                fill="fill"
                size="xs"
                type="info"
                htmlType="submit"
                loading={isLoading}
              >
                {t("EMPLOYEE.PENDING_MODAL.ACCEPT_BUTTON")}
              </Button>
            </div>
            <div className="m-5">
              <InputText
                {...register(EXTRA_RESPONSIBILITY.PASSWORD)}
                placeholder={t("EMPLOYEE.PENDING_MODAL.PASSWORD.PLACEHOLDER")}
                error={errors as any}
                size="sm"
                type="password"
              />
            </div>
          </form>
        </FormProvider>
      </>
    );
  };

  return (
    <Modal isOpen={isModalOpen} onClose={action} overlay closeAble>
      <div className="px-10 pb-10 modal_width">
        <SectionTitle label="EMPLOYEE.ACTION_BUTTONS.PENDING_ACCOUNT" />
        <div className="d-flex flex-column gap-8">
          {pendingData.map((item: any, i: number) => (
            <PendingAccount data={item} key={i} />
          ))}
        </div>
      </div>
    </Modal>
  );
};
